import { Currencies } from '@/entities/curency'
import { BaseBageLogo } from '@/shared/components'
import { Skeleton } from '@/shared/components/shadcn'
import { formatCurrency } from '@/shared/lib/currency'
import React, { ReactElement } from 'react'
import { TraderBalanceAndChannelsModel } from '../../model'
import { bankNameToTypeMap, bankOptions, methodOptions } from 'syncra-storybook/shared/utils'

type OptionsModel = {
  logo: ReactElement
  name: string
}

export const PayoutsSettingsBanner = ({
  data,
  isLoading,
  isError,
}: {
  data: TraderBalanceAndChannelsModel | undefined
  isLoading: boolean
  isError: boolean
}) => {
  const columnClasses =
    'flex flex-col flex-1 gap-2 justify-between border-b md:border-b-0 md:border-r last:border-0 border-card-border dark:border-card-border-dark pb-4 md:pb-0 last:pb-0 md:pr-4 md:last:pr-0 w-full md:max-w-[33%]'
  const labelClasses = 'text-sm text-text-secondary leading-[24px]'
  const dataClasses = 'font-medium'

  const banks: OptionsModel[] =
    data?.banks
      .filter((bank) => bank && bank !== '')
      .map((bank) => {
        // Маппим английское название на enum
        const bankEnum = bankNameToTypeMap[bank]
        if (!bankEnum) {
          console.warn(`Bank "${bank}" not found in mapping`)
          return null
        }
        const bankOption = bankOptions[bankEnum]
        if (!bankOption) {
          console.warn(`Bank option for "${bankEnum}" not found`)
          return null
        }
        return {
          name: bankOption.title,
          logo: bankOption.icon,
        }
      })
      .filter((bank): bank is OptionsModel => bank !== null) ?? []

  const methods: OptionsModel[] =
    data?.methods.map((method) => ({
      name: methodOptions[method]?.title ?? '',
      logo: methodOptions[method]?.icon ?? '',
    })) ?? []

  return (
    <div className="flex md:flex-row flex-col justify-between gap-4 md:gap-6 bg-light-purple dark:bg-dark-purple mb-[30px] md:mb-10 p-6 rounded-2xl">
      <div className={columnClasses}>
        <span className={labelClasses}>Баланс для выплат</span>
        {isLoading || data === undefined ? (
          <Skeleton className="w-2/3 h-8" />
        ) : isError ? (
          <div>Ошибка загрузки данных.</div>
        ) : (
          // <BaseProgressBar currentBalance={value1} totalBalance={data.availableAmount ?? 0} />
          <div className="font-medium text-base">
            {formatCurrency(data.availableAmount, Currencies.RUB)}
          </div>
        )}
      </div>

      <div className={columnClasses}>
        <span className={labelClasses}>Методы</span>
        {isLoading || data === undefined ? (
          <Skeleton className="w-2/3 h-8" />
        ) : isError ? (
          <div>Ошибка загрузки данных.</div>
        ) : (
          <span className={`${dataClasses} flex gap-2 overflow-x-auto scrollbar-hide`}>
            {methods.length !== 0 ? (
              <>
                {methods.map((method, i) => (
                  <BaseBageLogo key={i} logo={method.logo} name={method.name} />
                ))}
              </>
            ) : (
              'Не выбраны'
            )}
          </span>
        )}
      </div>

      <div className={columnClasses}>
        <div className="flex items-center gap-1">
          <span className={labelClasses}>Банки</span>
          <span className="text-primary">
            {isLoading ? (
              <Skeleton className="w-3 h-4" />
            ) : (data?.banks as string[])?.includes('') ? null : (
              data?.banks.length
            )}
          </span>
        </div>
        {isLoading || data === undefined ? (
          <Skeleton className="w-2/3 h-8" />
        ) : isError ? (
          <div>Ошибка загрузки данных.</div>
        ) : (
          <div
            className={`${dataClasses} max-w-[500px] flex overflow-x-auto whitespace-nowrap scrollbar-hide`}
          >
            {(data.banks as string[]).includes('') ? (
              'Все банки'
            ) : banks.length !== 0 ? (
              <>
                {banks.map((bank, index) => (
                  <React.Fragment key={index}>
                    <BaseBageLogo logo={bank.logo} name={bank.name} />
                    {index !== banks.length - 1 ? <span>,&nbsp;</span> : null}
                  </React.Fragment>
                ))}
              </>
            ) : (
              'Не выбраны'
            )}
          </div>
        )}
      </div>
    </div>
  )
}
